import { isNullOrUndefined } from "./any";

export function formatLicensePlate(value: string | null | undefined): string {
  return value?.replace(/\s+/g, "").toUpperCase() ?? "";
}

export function sanitizeLicenseplate(value: string | null): string | null {
  if (isNullOrWhiteSpace(value)) {
    return null;
  }

  const licensePlateDisallowedCharacters = [",", ".", ":", ";", "-"];
  for (const current of licensePlateDisallowedCharacters) {
    if (value.includes(current)) {
      return null;
    }
  }

  if (value.length < 2 || value.length > 15) {
    return null;
  }

  return value.toUpperCase();
}

export function extractDigits(value: string | null | undefined) {
  if (!value) {
    return "";
  }
  const matches = value.match(/\d+/g);
  return matches ? matches.join("") : "";
}

export function isNullOrWhiteSpace(value: string | null | undefined): value is null | undefined | "" {
  if (isNullOrUndefined(value) || typeof value !== "string") {
    return true;
  }

  const whiteSpace = " ";
  for (const current of value) {
    if (current !== whiteSpace) {
      return false;
    }
  }

  return true;
}

// https://medium.freecodecamp.com/three-ways-to-title-case-a-sentence-in-javascript-676a9175eb27#.25m5eqft3
export function toTitleCase(value: string) {
  if (isNullOrWhiteSpace(value)) {
    return null;
  }
  return value
    .toLowerCase()
    .split(" ")
    .map(word => (word ? word.replace(word[0], word[0].toUpperCase()) : ""))
    .join(" ");
}

export function toPersonNameCase(value: string | null | undefined): string {
  if (typeof value !== "string") {
    return "";
  }
  const normalized = value.trim().replace(/\s+/g, " ");
  if (normalized.length === 0) {
    return "";
  }
  const capitalize = (segment: string) => (segment ? segment[0].toUpperCase() + segment.slice(1) : "");
  const applyDelimiter = (input: string, delimiter: string) =>
    input
      .split(delimiter)
      .map(part => capitalize(part))
      .join(delimiter);
  const formatWord = (word: string) => applyDelimiter(applyDelimiter(word, "'"), "-");
  return normalized.toLowerCase().split(" ").map(formatWord).join(" ");
}

// data:image/jpeg;base64,${base64}
export async function base64ToBlob(base64: string) {
  const res = await fetch(base64);
  return await res.blob();
}

export function formatMacAddress(macAddress: string) {
  return (
    macAddress
      .match(/.{1,2}/gi)
      ?.splice(0, 6)
      .join(":") ?? ""
  );
}

export function isTrue(value: string | boolean | null | undefined) {
  return value === "true" || value === "1";
}

const stripHtmlRegex = /<(.|\n)*?>/g;

export function sanitizeText(text: string | null | undefined): string | null {
  if (!text || text.trim() === "") {
    return null;
  }
  const sanitizedText = removeHtml(text);
  return sanitizedText ? sanitizedText.replace(/\s+/g, " ").trim() : null;
}

export function removeHtml(html: string | null | undefined): string | null {
  if (!html || html.trim() === "") {
    return null;
  }

  let sanitized = html;
  let previousLength;
  let iterations = 0;
  const maxIterations = 1000;

  // Keep applying the regex until no more changes occur (multi-pass sanitization)
  do {
    previousLength = sanitized.length;
    sanitized = sanitized.replace(stripHtmlRegex, "");
    iterations++;
  } while (sanitized.length !== previousLength && iterations < maxIterations);

  // Throw exception if max iterations exceeded to prevent returning potentially vulnerable string
  if (iterations >= maxIterations) {
    throw new Error(
      "HTML sanitization exceeded maximum iterations limit. Input may contain malicious content designed to cause infinite loops."
    );
  }

  return sanitized;
}

export function containsHtml(inputString: string | null | undefined): boolean {
  if (!inputString || inputString.trim() === "") {
    return false;
  }
  return stripHtmlRegex.test(inputString);
}
